import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Stack,
  Chip,
  Grid,
  IconButton,
  Button,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';

/**
 * Composant card pour afficher un dispute dans une liste
 * Affiche: statut, parties impliquées, listing, résumé
 */
const DisputeListCard = ({ dispute, onOpen }) => {
  const { t } = useTranslation(['disputes', 'financial']);
  const { user: currentUser } = useAuth();
  const theme = useTheme();

  if (!dispute) return null;

  // Extraire l'ID du dispute
  const disputeId = dispute?.id || dispute?._id || dispute?.dispute_id || dispute?.disputeId;

  // Récupérer le statut
  const status = dispute?.status || 'unknown';
  const statusLabel = t(`disputes:status.${status}`, status);

  // Parties: always pick the user whose id != current user id
  const getId = (obj) =>
    obj?.id ||
    obj?._id ||
    obj?.userId ||
    obj?.user_id ||
    obj?.owner_id ||
    obj?.renter_id ||
    obj?.profile?.id ||
    obj?.profile?._id ||
    null;

  const currentUserId = getId(currentUser);

  const renterObj = dispute?.renter || (dispute?.renter_id ? { id: dispute.renter_id } : null);
  const ownerObj = dispute?.listing?.owner || (dispute?.listing?.owner_id ? { id: dispute.listing.owner_id } : null);
  const reporterObj =
    dispute?.reporter ||
    dispute?.reportedUser ||
    dispute?.reporter_user ||
    dispute?.reported_user ||
    null;

  const renterId = getId(renterObj) || dispute?.renter_id || dispute?.renterId || dispute?.renter_id;
  const ownerId =
    getId(ownerObj) || dispute?.listing?.owner_id || dispute?.owner_id || dispute?.ownerId || dispute?.owner_id;

  const isRenter = currentUserId && renterId && String(currentUserId) === String(renterId);
  const isOwner = currentUserId && ownerId && String(currentUserId) === String(ownerId);

  const candidates = [ownerObj, renterObj, reporterObj, dispute?.reportedUser, dispute?.reporter]
    .filter(Boolean)
    .filter((u) => getId(u));

  let otherUser = candidates.find((u) => {
    const id = getId(u);
    if (!id || !currentUserId) return false;
    return String(id) !== String(currentUserId);
  });

  // If we couldn't exclude current user (missing ids), fallback to something consistent
  if (!otherUser) {
    otherUser = isRenter ? ownerObj : isOwner ? renterObj : candidates[0] || null;
  }

  // Informations sur le listing — supporte plusieurs formes renvoyées par l'API
  const listing = dispute?.listing || dispute?.reservation?.listing || null;
  const listingTitle =
    listing?.title ||
    listing?.name ||
    dispute?.metadata?.listing_title ||
    t('disputes:unknownListing', 'Listing inconnu');

  // Reservation
  const reservation = dispute?.reservation;
  const reservationId = reservation?.id || dispute?.reservation_id;

  // Dates
  const createdAt = dispute?.created_at || dispute?.createdAt;
  const formattedDate = createdAt 
    ? format(new Date(createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })
    : '-';

  // Statut de couleur
  const statusColor = {
    pending: 'info',
    in_review: 'warning',
    resolved: 'success',
    cancelled: 'error',
    needs_support: 'warning',
    escalated: 'error',
    closed: 'default',
    pending_settlement: 'warning'
  }[status] || 'default';

  // Avatar autre partie
  const otherUserAvatar = otherUser?.profilePicture || otherUser?.avatar;
  const otherUserName = otherUser?.name || otherUser?.firstName || t('disputes:unknownUser', 'Utilisateur inconnu');
  const otherUserInitials = otherUserName?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || '?';

  // Compensation info
  const compensation = dispute?.compensation_amount ? ` ${dispute?.compensation_amount} ${dispute?.reservation?.currency || ''}` : '';

  const handleOpen = () => {
    if (onOpen) onOpen(dispute);
  };

  return (
    <Card
      sx={{
        mb: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)'
        },
        borderLeft: `4px solid ${theme.palette[statusColor]?.main}`,
        bgcolor: 'background.paper'
      }}
    >
      <CardContent>
        {/* En-tête: ID, Statut, Bouton ouvrir */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <GavelIcon sx={{ color: theme.palette[statusColor]?.main, fontSize: 24 }} />
              <Typography variant="h6" fontWeight={600}>
                #{disputeId}
              </Typography>
              <Chip
                label={statusLabel}
                size="small"
                color={statusColor}
                variant="filled"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {formattedDate}
            </Typography>
          </Box>
          <Tooltip title={t('common:viewDetails', 'Voir les détails')}>
            <IconButton size="small" color="primary" onClick={handleOpen} target="_blank">
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Grille de contenu */}
        <Grid container spacing={2}>
          {/* Parties impliquées */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                👥 {t('disputes:penalties.parties.implicated', 'Autres parties')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Avatar
                  src={otherUserAvatar}
                  sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 'bold' }}
                >
                  {!otherUserAvatar && otherUserInitials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600}
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {otherUserName}
                  </Typography>
                  
                </Box>
              </Stack>
            </Box>
          </Grid>

          {/* Listing */}
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                🏠 {t('disputes:listing', 'Listing')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <HomeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Tooltip title={listingTitle}>
                  <Typography 
                    variant="body2" 
                    noWrap
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                  >
                    {listingTitle}
                  </Typography>
                </Tooltip>
              </Stack>
            </Box>
          </Grid>

          {/* Réservation ID */}
          {reservationId && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  📋 {t('disputes:detail.reservationTitle', 'Réservation')}
                </Typography>
                <Typography 
                  variant="body2" 
                  fontWeight={500}
                  sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.9rem' }}
                >
                  ID: #{reservationId}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Compensation si applicable */}
          {dispute?.compensation_amount && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  💰 {t('disputes:amicable.compensationType', 'Compensation')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Chip 
                    label={compensation}
                    size="small"
                    variant="filled"
                    color={dispute?.compensation_type === 'owner_compensation' ? 'error' : 'success'}
                  />
                  {dispute?.compensation_type && (
                    <Typography variant="caption" color="text.secondary">
                      ({t(`disputes:compensationTypes.${dispute.compensation_type}`, dispute.compensation_type)})
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Résumé bas */}
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {status === 'resolved' 
              ? t('disputes:resolved', 'Litige résolu')
              : status === 'escalated'
              ? t('disputes:escalated', 'Escaladé - Support requis')
              : status === 'in_review'
              ? t('disputes:inReview', 'En révision')
              : t('disputes:pending', 'En attente')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={handleOpen}
            target="_blank"
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            {t('common:viewDetails', 'Voir')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DisputeListCard;
